import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kSettleOnce } from "../../../Admin/kReusableCoreTS/kSettleOnce";
import { WaCrypt15, Crypt15Result } from "./WaCrypt15";

// AppUnlockRun - the chosen backup, read off this device and unlocked on it.
//
// ==================================================================================================
// !! ⭐ THIS IS THE STEP THE WHOLE DESIGN EXISTS FOR
// ==================================================================================================
//
// The 64-digit key opens every backup the account will ever make and WhatsApp cannot rotate it. A
// server could do this work if the page posted the file and the key together - it would be less code
// and it would be worse: a key in a request body is a key in a log, a proxy buffer and a crash dump.
// Here it is typed into a box, used once, and never leaves the device.
//
// ⚠ AND THE FILE NO LONGER TRAVELS TWICE. The Drive version of this fetched the backup back DOWN
// from Drive before unlocking it, because the Google Picker was the only way `drive.file` could
// reach a file the app had not created. The browser reads the subscriber's own disk now, so the
// ciphertext never moves at all and only the unlocked database is sent anywhere.
//
// ==================================================================================================
// !! ⚠ `FileReader`, NOT `blob.arrayBuffer()`
// ==================================================================================================
//
// Not style: `arrayBuffer()` is a promise, and the house rule against promises here is not
// decoration either - it is what keeps every step of a long transfer a NAMED handler that can be
// followed in a stack trace. `FileReader` is the callback-shaped reader that has always existed, and
// it reports progress, which a promise cannot.
//
// ⚠ THE WHOLE FILE IS IN MEMORY, AND THAT IS ALREADY THE DESIGN. `WaCrypt15.Open` is one-shot -
// ciphertext, plaintext and inflated database are live at once - which is exactly what
// `ImportGate.MaxBytes` refuses above. Streaming it would need a streaming GCM, and a GCM tag that
// is only verified at the end cannot authenticate a stream that has already been written somewhere.

/** Where the run is now. `Percent` is -1 when the step has no meaningful measure. */
export type UnlockStep = (Say: string, Percent: number) => void;

/** The end: a database, or the sentence the subscriber sees. Never both. */
export type UnlockDone = (Db: Uint8Array | null, Why: string) => void;

interface UnlockOutcome {
    Db: Uint8Array | null;
    Why: string;
}

export class AppUnlockRun {
    private Blob: Blob;
    private Key: string;
    private Step: UnlockStep;
    private Done: UnlockDone;
    private Reader: FileReader | null = null;
    private Answer: kSettleOnce<UnlockOutcome> = new kSettleOnce<UnlockOutcome>(null);

    public constructor(Blob: Blob, Key: string, Step: UnlockStep, Done: UnlockDone) {
        this.Blob = Blob;
        this.Key = Key;
        this.Step = Step;
        this.Done = Done;
        this.Answer.Arm(this.Deliver);
    }

    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle(null, "WACommander cannot open a backup at the moment.");
            return;
        }
        try {
            if (this.Blob === null || this.Blob === undefined) {
                this.Settle(null, "No file was chosen.");
                return;
            }
            this.Step("Reading the file on this device…", 0);
            this.Reader = new FileReader();
            (this.Reader as FileReader).onprogress = this.OnProgress;
            (this.Reader as FileReader).onerror = this.OnReadFail;
            (this.Reader as FileReader).onload = this.OnRead;
            (this.Reader as FileReader).readAsArrayBuffer(this.Blob);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppUnlockRun.Start");
            this.Settle(null, "That file could not be read.");
        }
    };

    /**
     * ⚠ READING A LOCAL FILE IS NOT INSTANT ON A PHONE. A 60 MB file off slow storage is seconds of
     * nothing, and a quiet page at the last step of a sign-up reads as one that has hung.
     */
    private OnProgress = (E: ProgressEvent): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: a progress tick that fails must not end the read. The bytes
        // keep arriving either way; only the number on screen would be stale.
        try {
            if (E.lengthComputable !== true) { return; }
            this.Step("Reading the file on this device…",
                Math.floor((E.loaded / E.total) * 100));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppUnlockRun.OnProgress");
        }
    };

    /**
     * ⛔ A FAILED READ IS THE SUBSCRIBER'S SITUATION, NOT A DEFECT. A file moved or deleted between
     * the pick and the unlock, or a permission the browser withdrew, is an EXPECTED condition
     * (law 1, fate 1); reporting it would flip the gate and blank the page they are signing up on.
     */
    private OnReadFail = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: an expected condition, answered as a sentence they can act on.
        this.Settle(null, "That file could not be read from this device. Choose it again - if it"
            + " came from the phone, check it is still there.");
    };

    private OnRead = (): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle(null, "WACommander cannot open a backup at the moment.");
            return;
        }
        try {
            const Raw: ArrayBuffer = (this.Reader as FileReader).result as ArrayBuffer;
            this.Reader = null;
            if (Raw === null || Raw === undefined || Raw.byteLength === 0) {
                this.Settle(null, "That file is empty. If it was just written by the phone, give it"
                    + " a moment to finish and choose it again.");
                return;
            }
            this.Step("Unlocking it on this device…", -1);
            WaCrypt15.Open(new Uint8Array(Raw), this.Key, this.OnOpened);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppUnlockRun.OnRead");
            this.Settle(null, "That file could not be read.");
        }
    };

    /**
     * ⚠ `WaCrypt15` HAS ALREADY WRITTEN THE SENTENCE. Its refusals name the actual cause - a wrong
     * key, a crypt14, something that is not a database - and rewording them here would replace
     * three useful answers with one vague one.
     */
    private OnOpened = (Result: Crypt15Result): void => {
        if (getCoreUtils().KErrorMode === true) {
            this.Settle(null, "WACommander cannot open a backup at the moment.");
            return;
        }
        try {
            if (Result.Db === null) { this.Settle(null, nz(Result.Why)); return; }
            this.Settle(Result.Db, "");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppUnlockRun.OnOpened");
            this.Settle(null, "That backup could not be unlocked.");
        }
    };

    /** ⛔ ONE ANSWER. `FileReader` can fire both `error` and `load` on some failures. */
    private Settle = (Db: Uint8Array | null, Why: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - this is the only thing that answers the
        // caller, and a guard here would leave the page on "Unlocking..." for ever.
        try {
            this.Answer.Settle({ Db, Why });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppUnlockRun.Settle");
        }
    };

    // DOCUMENTED EXCEPTION: owner-invoked adapter. ERROR-SUPPRESSED-JUSTIFIED:
    // the shared latch closes before cleanup; cleanup/callback failures reach Settle's existing catch.
    private Deliver = (Outcome: UnlockOutcome): void => {
        this.Reader = null;
        this.Done(Outcome.Db, Outcome.Why);
    };
}
